// microphonePermissionManager.js

const TEST_ROUTES = [
  '/admin/practice/',
  '/admin/test',
  '/admin/sectionalTest',
  '/admin/fulltest/',
  '/admin/mock',
  '/admin/pending',
  '/admin/extensive/new-sectional-tests',
  '/admin/extensive-pending/new-sectional-tests',
  '/admin/test-material'
];

const THEME_APPLY_ROUTES = [
  '/admin/test',
  '/admin/sectionalTest',
  '/admin/fulltest/',
];

// Global registry for active microphone streams and audio contexts
let activeStreams = [];
let activeAudioContexts = [];
let activeAnalysers = [];
let activeAnimationFrames = [];
let activeSources = []; // ✅ Track MediaStreamSources

export const isTestRoute = (pathname) => {
  return TEST_ROUTES.some(route => pathname.includes(route));
};

export const isThemeRoute = (pathname) => {
  return THEME_APPLY_ROUTES.some(route => pathname.includes(route));
};

// Register an active microphone stream (call after getUserMedia)
export const registerMicrophoneStream = (stream) => {
  // console.log(stream);
  if (stream && !activeStreams.includes(stream)) {
    activeStreams.push(stream);
  }
};

// Register AudioContext
export const registerAudioContext = (audioContext) => {
  if (audioContext && !activeAudioContexts.includes(audioContext)) {
    activeAudioContexts.push(audioContext);
  }
};

// Register MediaStreamSource
export const registerMediaStreamSource = (source) => {
  if (source && !activeSources.includes(source)) {
    activeSources.push(source);
  }
};

// Register Analyser
export const registerAnalyser = (analyser) => {
  if (analyser && !activeAnalysers.includes(analyser)) {
    activeAnalysers.push(analyser);
  }
};

// Register Animation Frame
export const registerAnimationFrame = (frameId) => {
  if (frameId && !activeAnimationFrames.includes(frameId)) {
    activeAnimationFrames.push(frameId);
  }
};

// Unregister a stream when it is stopped manually
export const unregisterMicrophoneStream = (stream) => {
  activeStreams = activeStreams.filter(s => s !== stream);
};

export const unregisterAudioContext = (audioContext) => {
  activeAudioContexts = activeAudioContexts.filter(ctx => ctx !== audioContext);
};

export const unregisterMediaStreamSource = (source) => {
  activeSources = activeSources.filter(s => s !== source);
};

export const unregisterAnalyser = (analyser) => {
  activeAnalysers = activeAnalysers.filter(a => a !== analyser);
};

export const unregisterAnimationFrame = (frameId) => {
  activeAnimationFrames = activeAnimationFrames.filter(id => id !== frameId);
};

export const stopAllMicrophoneStreams = async () => {
  // 1. Cancel all animation frames
  activeAnimationFrames.forEach(frameId => {
    try {
      cancelAnimationFrame(frameId);
    } catch (error) {
      console.warn('Mic cleanup error:', error);
    }
  });
  activeAnimationFrames = [];

  // 2. Disconnect all MediaStreamSources
  activeSources.forEach(source => {
    try {
      source.disconnect();
    } catch (error) {
      console.warn('Mic cleanup error:', error);
    }
  });
  activeSources = [];

  // 3. Disconnect all analysers
  activeAnalysers.forEach(analyser => {
    try {
      analyser.disconnect();
    } catch (error) {
      console.warn('Mic cleanup error:', error);
    }
  });
  activeAnalysers = [];

  // 4. Stop all media tracks
  activeStreams.forEach(stream => {
    try {
      // stream.getTracks().forEach(track => {
      //   track.enabled = false;
      //   track.stop();
      // });
      if ( stream && typeof stream === "object" && typeof stream.getTracks === "function" ) {
        stream.getTracks().forEach((track) => {
          if (track) {
            track.enabled = false;
            track.stop();
          }
        });
      } else {
        console.warn("Skipped invalid stream:", stream, typeof stream);
      }
    } catch (error) {
      console.warn('Mic cleanup error:', error);
    }
  });
  activeStreams = [];

  // 5. Close all AudioContexts
  const closePromises = activeAudioContexts.map(audioContext => {
    try {
      if (audioContext.state !== 'closed') {
        return audioContext.close().catch(err => {
          console.warn('AudioContext close error:', err);
        });
      }
    } catch (error) {
      console.warn('Mic cleanup error:', error);
    }
    return Promise.resolve();
  });
  
  await Promise.all(closePromises);
  activeAudioContexts = [];
};

// Keep old function name for backward compatibility
export const revokeMicrophonePermission = stopAllMicrophoneStreams;

// Ensure light theme on test pages
export const ensureLightTheme = () => {
  const root = document.documentElement;
  if (root.getAttribute("data-theme") === "dark") {
    root.setAttribute("data-theme", "light");
  }
};

export const enforceFullscreenByRoute = (pathname) => {
  const isTestRoute = THEME_APPLY_ROUTES.some(route =>
    pathname.startsWith(route)
  );

  const isCurrentlyFullscreen = !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );

  // If NOT a test route but fullscreen is active → exit
  if (!isTestRoute && isCurrentlyFullscreen) {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  }
};